import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Icon from '@mdi/react';
import { mdiMapMarker } from '@mdi/js';
import { loadGoogleMapsScript } from '../utils/loadGoogleMaps';
import './LocationPickerModal.css';

/**
 * LocationPickerModal - Reusable Google Places Location Picker
 *
 * Features:
 * - Google Places Autocomplete for address selection
 * - Display current location with edit capability
 * - Capture description, address, latitude, longitude
 * - Used for both travel destinations and home location settings
 *
 * Props:
 * - isOpen: boolean - Modal open state
 * - onClose: function - Close modal callback
 * - onSave: function(locationData) - Save location callback
 * - currentLocation: object | null - Current location { description, address, latitude, longitude }
 * - mode: 'travel' | 'home' - UI mode hints
 * - title: string - Modal title
 */
const LocationPickerModal = ({ isOpen, onClose, onSave, currentLocation = null, mode = 'travel', title = 'Select Location' }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [, setUseManualEntry] = useState(false);
    const [, setManualLocationName] = useState('');
    const [autocompleteError, setAutocompleteError] = useState(false);

    const autocompleteRef = useRef(null);
    const searchInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const hasShownSuggestionsRef = useRef(false);

    const hasCurrentLocation = currentLocation && currentLocation.description;

    // Modes that capture a full street address (teaching venue, event address) vs.
    // city-only modes (travel destination, home location).
    const wantsFullAddress = mode === 'address' || mode === 'teaching';

    // Auto-enter editing mode when modal opens without a current location
    useEffect(() => {
        if (isOpen && !hasCurrentLocation && !isEditing) {
            setIsEditing(true);
        }
    }, [isOpen, hasCurrentLocation, isEditing]);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            // Prevent body scrolling while modal is open
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = '15px'; // Compensate for scrollbar width

            return () => {
                // Restore body scrolling when modal closes
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            };
        }
    }, [isOpen]);

    // Load Google Maps and Initialize Places Autocomplete
    useEffect(() => {
        if (!isOpen || !isEditing) {
            return;
        }

        let isMounted = true;

        const initializeAutocomplete = async () => {
            try {
                // Load Google Maps script dynamically
                await loadGoogleMapsScript();

                if (!isMounted) return;

                if (!searchInputRef.current) {
                    throw new Error('Search input element not found');
                }

                // Create PlaceAutocompleteElement web component
                const autocompleteElement = document.createElement('gmp-place-autocomplete');
                // Set autocomplete type based on mode: full address for teaching venues / event
                // addresses, city only for travel destinations and home location.
                const autocompleteType = wantsFullAddress ? 'address' : 'city';
                autocompleteElement.setAttribute('type', autocompleteType);

                // Set placeholder based on mode
                const placeholder = wantsFullAddress
                    ? 'Type a full address (e.g., 123 Main St, Brisbane QLD)'
                    : 'Type a city name (e.g., Adelaide, Australia)';
                autocompleteElement.setAttribute('placeholder', placeholder);
                autocompleteElement.id = 'location-search';
                autocompleteElement.className = 'location-search-input';

                // Replace the placeholder input with the autocomplete element
                const inputParent = searchInputRef.current.parentElement;
                inputParent.replaceChild(autocompleteElement, searchInputRef.current);

                // Store reference to the web component
                autocompleteRef.current = autocompleteElement;
                searchInputRef.current = autocompleteElement;

                // Listen for place selection (gmp-select is the correct event for web component)
                autocompleteElement.addEventListener('gmp-select', async ({ placePrediction }) => {
                    hasShownSuggestionsRef.current = true;

                    try {
                        // Convert prediction to place and fetch fields
                        const place = placePrediction.toPlace();
                        await place.fetchFields({
                            fields: ['displayName', 'formattedAddress', 'location', 'id', 'addressComponents']
                        });

                        // Extract description based on mode
                        let description;
                        if (wantsFullAddress) {
                            // For full-address modes (teaching venue, event address), use the
                            // full formatted address
                            description = place.formattedAddress || place.displayName;
                        } else {
                            // For city mode (travel/home), extract city name from address components
                            const cityComponent = place.addressComponents?.find(
                                comp => comp.types.includes('locality') || comp.types.includes('administrative_area_level_1')
                            );
                            description = cityComponent?.longText || place.displayName || place.formattedAddress;
                        }

                        const locationData = {
                            description: description,
                            address: place.formattedAddress,
                            latitude: place.location?.lat(),
                            longitude: place.location?.lng(),
                            placeId: place.id
                        };

                        setSelectedPlace(locationData);
                        setSearchInput(locationData.description);
                        setError(null);
                    } catch (err) {
                        console.error('❌ Error fetching place details:', err);
                        setError('Failed to get location details. Please try again.');
                    }
                });
            } catch (err) {
                console.error('❌ Error loading Google Maps:', err);

                if (isMounted) {
                    setAutocompleteError(true);
                    setUseManualEntry(true);
                    setError('Google Places autocomplete is temporarily unavailable. Please enter your location manually.');
                }
            }
        };

        initializeAutocomplete();

        return () => {
            isMounted = false;
            if (autocompleteRef.current && window.google) {
                window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
            }
        };
    }, [isOpen, isEditing, wantsFullAddress]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setIsEditing(false);
            setSelectedPlace(null);
            setSearchInput('');
            setError(null);
            setUseManualEntry(false);
            setManualLocationName('');
            setAutocompleteError(false);
            hasShownSuggestionsRef.current = false;
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        }
    }, [isOpen]);

    const handleEdit = () => {
        setIsEditing(true);
        setSearchInput('');
        setSelectedPlace(null);
        setError(null);
    };

    const handleCancel = () => {
        if (isEditing && hasCurrentLocation) {
            setIsEditing(false);
            setSearchInput('');
            setSelectedPlace(null);
        } else {
            onClose();
        }
    };

    const handleSave = async () => {
        // If autocomplete error detected and user has entered text manually
        if (autocompleteError && searchInput.trim()) {
            setIsLoading(true);
            setError(null);

            try {
                const locationData = {
                    description: searchInput.trim(),
                    address: null,
                    latitude: null,
                    longitude: null
                };

                await onSave(locationData);
                onClose();
            } catch (err) {
                console.error('Error saving location:', err);
                setError(err.message || 'Failed to save location. Please try again.');
            } finally {
                setIsLoading(false);
            }
            return;
        }

        // Autocomplete mode - require selected place
        if (!selectedPlace) {
            setError('Please select a location from the autocomplete suggestions');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Send Google Places location data (description, address, latitude, longitude)
            const locationData = {
                description: selectedPlace.description,
                address: selectedPlace.address,
                latitude: selectedPlace.latitude,
                longitude: selectedPlace.longitude
            };

            await onSave(locationData);
            onClose();
        } catch (err) {
            console.error('Error saving location:', err);
            setError(err.message || 'Failed to save location. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const showCurrentLocation = hasCurrentLocation && !isEditing;
    const showSearch = !hasCurrentLocation || isEditing;

    const modalContent = (
        <div className="location-picker-modal-overlay" onClick={onClose}>
            <div className="location-picker-modal" onClick={(e) => e.stopPropagation()}>
                <div className="location-picker-modal-header">
                    <div className="location-picker-modal-header-content">
                        <Icon path={mdiMapMarker} size={1.2} color="var(--dl-color-purple-900)" />
                        <h2>{title}</h2>
                    </div>
                    <button type="button" className="location-picker-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="location-picker-modal-body">
                    {error && (
                        <div className="location-error-message">
                            {error}
                        </div>
                    )}

                    {/* Current Location Display */}
                    {showCurrentLocation && (
                        <div className="current-location-card">
                            <div className="current-location-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="var(--dl-color-purple-default)"/>
                                </svg>
                            </div>
                            <div className="current-location-content">
                                <div className="current-location-title">Current Location</div>
                                <div className="current-location-description">
                                    {typeof currentLocation.description === 'string'
                                        ? currentLocation.description
                                        : JSON.stringify(currentLocation.description)}
                                </div>
                                {currentLocation.address && typeof currentLocation.address === 'string' && (
                                    <div className="current-location-address">{currentLocation.address}</div>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn-edit-location"
                                onClick={handleEdit}
                                title="Edit location"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                                </svg>
                            </button>
                        </div>
                    )}

                    {/* Location Search Input - PlaceAutocompleteElement will be inserted here */}
                    {showSearch && (
                        <div className="location-search-section">
                            <label htmlFor="location-search" className="location-search-label">
                                {mode === 'home' ? 'Search for your home location' :
                                 mode === 'address' ? 'Enter event location address' :
                                 mode === 'teaching' ? 'Enter teaching venue address' :
                                 'Search for travel destination (City Only)'}
                            </label>
                            <span className="location-search-hint" style={{marginBottom: '8px', display: 'block'}}>
                                {wantsFullAddress
                                    ? 'Start typing a full address and select from suggestions'
                                    : 'Start typing a city or region and select from suggestions'}
                            </span>
                            {/* Placeholder input - will be replaced by gmp-place-autocomplete.
                                Stays a controlled input in the manual-fallback path (Google failed). */}
                            <input
                                ref={searchInputRef}
                                type="text"
                                id="location-search"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder={wantsFullAddress
                                    ? 'Type a full address (e.g., 123 Main St, Brisbane QLD)'
                                    : 'Type a city name (e.g., Adelaide, Australia)'}
                                className="location-search-input"
                            />
                        </div>
                    )}

                    {/* Selected Location Preview */}
                    {selectedPlace && (
                        <div className="selected-location-preview">
                            <div className="preview-label">Selected Location:</div>
                            <div className="preview-description">{selectedPlace.description}</div>
                            <div className="preview-address">{selectedPlace.address}</div>
                        </div>
                    )}
                </div>

                <div className="location-picker-modal-footer">
                    <button
                        type="button"
                        className="btn-cancel"
                        onClick={handleCancel}
                        disabled={isLoading}
                    >
                        {isEditing && hasCurrentLocation ? 'Back' : 'Cancel'}
                    </button>
                    {showSearch && (
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={handleSave}
                            disabled={isLoading || (!selectedPlace && !autocompleteError) || (autocompleteError && !searchInput.trim())}
                        >
                            {isLoading ? 'Saving...' : 'Save Location'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    // Render modal using React Portal to ensure position: fixed works correctly
    return ReactDOM.createPortal(modalContent, document.body);
};

export default LocationPickerModal;
